import { deleteObject, getDownloadURL, ref, uploadBytes, getMetadata } from 'firebase/storage';

import { storage } from '../firebase';
import { generateStorageFileName } from '../utils/utilsFiles';

export interface FileMetadata {
  contentType: string;
  customMetadata: Record<string, string>;
  displayName: string;
  size: string;
  timeCreated: string;
  updated: string;
}

export interface AttachmentMutators {
  storeFile: (
    file: File,
    storageLocation: string,
    storageFilePrefix?: string | null,
    storageFileSuffix?: string | null,
    metadata?: FileMetadata
  ) => Promise<{ fileName: string; url: string }>;
  deleteFile: (storageLocation: string, fileName: string) => Promise<void>;
  getFileMetadata: (storageLocation: string, fileName: string) => Promise<FileMetadata | null>;
}

export function useAttachmentMutators(): AttachmentMutators {
  const storeFile = async (
    file: File,
    storageLocation: string,
    storageFilePrefix?: string | null,
    storageFileSuffix?: string | null,
    metadata?: FileMetadata
  ): Promise<{ fileName: string; url: string }> => {
    console.log('storeFile: ', file, storageLocation, storageFilePrefix, storageFileSuffix, metadata);
    const storageFileName = generateStorageFileName(storageFilePrefix, storageFileSuffix);
    const fileRef = ref(storage, `${storageLocation}/${storageFileName}`);

    try {
      const snapshot = await uploadBytes(fileRef, file, {
        contentType: metadata?.contentType || file.type,
        customMetadata: metadata?.customMetadata,
      });
      const url = await getDownloadURL(snapshot.ref);
      return { fileName: storageFileName, url };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const deleteFile = async (storageLocation: string, fileName: string): Promise<void> => {
    const fileRef = ref(storage, `${storageLocation}/${fileName}`);
    try {
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  };

  const getFileMetadata = async (storageLocation: string, fileName: string): Promise<FileMetadata | null> => {
    const fileRef = ref(storage, `${storageLocation}/${fileName}`);
    try {
      const metadata = await getMetadata(fileRef);
      return {
        contentType: metadata.contentType || '',
        customMetadata: metadata.customMetadata || {},
        displayName: metadata.customMetadata?.displayName || fileName,
        size: metadata.size?.toString() || '0',
        timeCreated: metadata.timeCreated || new Date().toISOString(),
        updated: metadata.updated || new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error getting file metadata:', error);
      return null;
    }
  };

  return {
    storeFile,
    deleteFile,
    getFileMetadata,
  };
}
